import React, {
  forwardRef, useRef, useEffect, useCallback, ComponentType, InputHTMLAttributes, ForwardedRef, useContext,
} from 'react';
import { FixedKeyboardEvent } from 'ia-react-core/~/interfaces/FixedKeyboardEvent';
import { StyledInput } from './Input.styles';
import setRef from '../../../utils/components/_setRef';
import UnionControlGroupContext from '../_UnionControlGroupContext';

export interface InputProps<E extends Element = HTMLInputElement> extends Omit<InputHTMLAttributes<E>, 'onKeyDown' | 'ref'>{
  autoFocus?: boolean;
  tag?: string | ComponentType<any>;
  shaded?: boolean;
  ref?: ForwardedRef<E>;
  isValid?: boolean;
  isTouched?: boolean;
  hasWarning?: boolean;
  hasCaution?: boolean;
  hasInfo?: boolean;
  onKeyDown?: (event: FixedKeyboardEvent) => void;
}

const Input = Object.assign(
  forwardRef(<E extends Element = HTMLInputElement>({ autoFocus, tag, ...props }: InputProps<E>, ref: ForwardedRef<E>) => {
    const inputRef = useRef<E>();

    const { isUnionControlGroup } = useContext(UnionControlGroupContext);

    const mergeRefs = useCallback((current: E) => {
      setRef(inputRef, current);
      setRef(ref, current);
    }, [inputRef, ref]);

    useEffect(() => {
      if (autoFocus && inputRef?.current && ('focus' in inputRef.current)) (inputRef?.current as unknown as HTMLInputElement).focus();
    }, [autoFocus, inputRef]);

    return (
      <StyledInput
        type="input"
        ref={mergeRefs}
        as={tag}
        isUnionControlGroup={isUnionControlGroup}
        {...props}
      />
    );
  }),
  {
    StyledInput,
  },
);

export default Input;
